import React, { useEffect, useCallback, useState, useRef } from 'react';
import {
    ModalNext,
    ModalFooter,
} from '@jutro/components';
import { WMICButton } from 'wmic-components-platform-react';
import { WMICRichTextUtil, WMICTopics } from 'wmic-portals-utils-js';
import { useTranslator } from '@jutro/locale';
import { publish } from '@jutro/events';
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import { useAuthentication } from 'wmic-digital-auth-react';
import { AccountBillingDetailsService } from 'gw-capability-billing';
import styles from './WMICOneIncPaymentModal.module.scss';
import messages from './WMICOneIncPaymentModal.messages';

const { oneIncConfig } = appConfig;

const WMICOneIncPaymentModal = (props) => {
    const {
        oneIncPaymentDetails,
        paymentModalClosed,
        saveComplete,
        paymentModalLoaded,
        payNowEnabled,
        savePaymentEnabled,
        managePaymentMethodsEnabled,
        updateModalLoaded
    } = props;
    
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const [modalLoadErrorHandled, updateModalLoadErrorHandled] = useState(false);
    const [maskBackgroundBeforeLoading, setMaskBackgroundBeforeLoading] = useState(false);
    const portalOne = useRef(null);
    const pendingPaymentRef = useRef(null);
    const launchPaymentIfReady = () => {
        if(portalOne.current && pendingPaymentRef.current){
            portalOne.current.makePayment(pendingPaymentRef.current);
            pendingPaymentRef.current = null;
        }
    }
    let oneInc = null;
    let methodSaved = false;
    let portalOneContainer;
    const modalLoaded = useRef(false);

    const handleError = () => {
        if (!modalLoadErrorHandled) {
            updateModalLoadErrorHandled(true);
        }
    };

    const handleClose = () => {
        paymentModalClosed();
    };

    const setBodyScrolling = (value) => {
        if (!value) {
            document.body.classList.add("noscroll");
        } else {
            document.body.classList.remove("noscroll");
        }
    }

    const onLoad = (e) => {
        console.log('[OneINC] onLoad event:', e?.detail);
        setMaskBackgroundBeforeLoading(false);
    }

    const onUnload = (e) => {
        console.log('[OneINC] onUnload event:', e?.detail);
        updateModalLoaded(false);
        paymentModalClosed(methodSaved);
        setBodyScrolling(true);
    }

    const onError = (e) => {
        console.error(`[OneINC] Error event - Description: ${e?.detail?.Description}, Full detail:`, e?.detail);
    }

    const onSaveComplete = (e) => {
        console.log('[OneINC] onSaveComplete event:', e?.detail);
        methodSaved = true;
        saveComplete(e.detail);
    }

    const onPaymentCanceled = (e) => {
        console.log('[OneINC] onPaymentCanceled event:', e?.detail);
        if (e.srcElement) {
            e.srcElement.innerHTML = '';
        }
        paymentModalClosed({eventType: 'canceled'});
    }

    const onModalDialogOpened = (e) => {
        console.log('[OneINC] onModalDialogOpened event:', e?.detail);
        updateModalLoaded(true);
        modalLoaded.current = true;
        paymentModalLoaded();
    }

    const initEventHandlers = useCallback((container) => {
        if (!container) {
            return;
        }
        
        container.addEventListener('portalOne.load', onLoad);
        container.addEventListener('portalOne.unload', onUnload);
        container.addEventListener('portalOne.error', onError);
        container.addEventListener('portalOne.saveComplete', onSaveComplete);
        container.addEventListener('portalOne.paymentCanceled', onPaymentCanceled);
        container.addEventListener('portalOne.modalDialogOpened', onModalDialogOpened);
        
    }, []);

    const removeHttp = (url) => {
        return url.replace(/^https?:\/\//, '');
    };

    const modalLoadTimeout = () => {
        setTimeout(() => {
            if (!modalLoaded.current) {
                handleError();
            }
        }, oneIncConfig.timeout);
    };

    useEffect(() => {
        let mounted = true;

        const handleOneIncReady = () => {
            if (!mounted) return;
            // eslint-disable-next-line react-hooks/exhaustive-deps
            oneInc = window.OneInc;
            portalOneContainer = document.getElementById('portalOneContainer');
            if (!portalOneContainer) {
                const portalOneDiv = document.createElement("div");
                portalOneDiv.setAttribute("id", "portalOneContainer");
                document.body.appendChild(portalOneDiv);
                portalOneContainer = document.getElementById('portalOneContainer');
            }
            portalOne.current = new oneInc.PortalOne(portalOneContainer, initEventHandlers);
            launchPaymentIfReady();
        };
        const handleOneIncFailed = () => {
            if (!mounted) return;
            handleError();
        };

        if (window.__oneIncReady || window.OneInc?.PortalOne) {
            handleOneIncReady();
        } else if (window.__oneIncFailed) {
            handleOneIncFailed();
        } else {
            window.addEventListener('oneIncReady', handleOneIncReady);
            window.addEventListener('oneIncFailed', handleOneIncFailed);
        }

        return () => {
            mounted = false;
            window.removeEventListener('oneIncReady', handleOneIncReady);
            window.removeEventListener('oneIncFailed', handleOneIncFailed);
            if (portalOneContainer) {
                portalOneContainer.removeEventListener('portalOne.load', onLoad);
                portalOneContainer.removeEventListener('portalOne.unload', onUnload);
                portalOneContainer.removeEventListener('portalOne.error', onError);
                portalOneContainer.removeEventListener('portalOne.saveComplete', onSaveComplete);
                portalOneContainer.removeEventListener('portalOne.paymentCanceled', onPaymentCanceled);
                portalOneContainer.removeEventListener('portalOne.modalDialogOpened', onModalDialogOpened);
                setBodyScrolling(true);
                portalOneContainer.remove();
            }
        };
    }, []);

    useEffect(() => {
        if (payNowEnabled) {
            setMaskBackgroundBeforeLoading(true);
            setBodyScrolling(false);
            modalLoadTimeout();
            const todaysDate = new Date();
            const maxBankPaymentDaysInFuture = 60;
            const finalPayDate = new Date(Date.UTC(
                todaysDate.getFullYear(),
                todaysDate.getMonth(),
                todaysDate.getDate() + maxBankPaymentDaysInFuture,
                todaysDate.getHours(),
                todaysDate.getMinutes(),
                todaysDate.getSeconds())
            );
            
            const getOneIncSessionDetails = AccountBillingDetailsService.getOneIncSessionDetails(oneIncPaymentDetails.accountNumber, oneIncPaymentDetails.contactPublicId, oneIncPaymentDetails.policyId, oneIncPaymentDetails.termNumber, authHeader);
            getOneIncSessionDetails.then((response) => {
                if (!response.oneIncCustomerID || !response.sessionID) {
                    handleError();
                    return;
                }

                const billingAddressStreet = `${oneIncPaymentDetails.billingAddress.addressLine1}, ${oneIncPaymentDetails.billingAddress.city}, ${oneIncPaymentDetails.billingAddress.state}`;
                const paymentObject = {
                    customerId: response.oneIncCustomerID,
                    sessionId: response.sessionID,
                    paymentCategory: oneIncPaymentDetails.creditCardAllowed ? 'UserSelect' : 'ECheck',
                    acceptCreditCards: oneIncPaymentDetails.creditCardAllowed ? 'true' : 'false',
                    feeContext: 'PaymentWithFee',
                    convenienceFeeType: response.feeTypeValueOneInc_Ext,
                    amountContext: 'SelectOrEnterAmount',
                    minAmountDue: oneIncPaymentDetails.minAmountDue,
                    accountBalance: oneIncPaymentDetails.accountBalance,
                    billingZip: oneIncPaymentDetails.billingAddress.postalCode,
                    billingAddressStreet: billingAddressStreet,
                    policyHolderName: oneIncPaymentDetails.userDisplayName,
                    isScheduledPayEnabled: 'true',
                    finalPayDate: finalPayDate,
                    clientReferenceData1: oneIncPaymentDetails.clientReferenceData1,
                    clientReferenceData2: oneIncPaymentDetails.policyId,
                    email: oneIncPaymentDetails.email,
                    clientReferenceData4: 'AMP',
                    clientReferenceData5: removeHttp(response.bcUrl),
                    confirmationDisplay: 'true',
                    saveOption: 'UserSelect',
                    displayMode: 'Modal',
                    allowClosing: true
                };
                
                console.log('[OneINC] Calling makePayment with:', paymentObject);
                pendingPaymentRef.current = paymentObject;
                launchPaymentIfReady();
            }, (error) => {
                handleError(error);
            }).catch((error) => {
                handleError(error);
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payNowEnabled]);

    useEffect(() => {
        if (savePaymentEnabled) {
            modalLoadTimeout();
            const getOneIncSessionDetails = AccountBillingDetailsService.getOneIncSessionDetails(oneIncPaymentDetails.accountNumber, oneIncPaymentDetails.contactPublicId, null, null, authHeader);
            getOneIncSessionDetails.then((response) => {
                if (!response.sessionID) {
                    handleError();
                    return;
                }
                portalOne.current.managePaymentMethods({
                    customerId: response.oneIncCustomerID,
                    sessionId: response.sessionID,
                    acceptCreditCards: 'false',
                    acceptPrepaidCards: 'false',
                    paymentCategory: 'ECheck',
                    clientReferenceData1: oneIncPaymentDetails.invoiceStreamId,
                    clientReferenceData2: oneIncPaymentDetails.policyId,
                    clientReferenceData4: 'AMP',
                    clientReferenceData5: removeHttp(response.bcUrl),
                    policyHolderName: oneIncPaymentDetails.userDisplayName,
                    allowClosing: true,
                    displayMode: 'Modal',
                    saveOption: 'UserSelect'
                });
                
                console.log('[OneINC] Calling managePaymentMethods (save)');
                
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savePaymentEnabled]);

    useEffect(() => {
        if (managePaymentMethodsEnabled) {
            setMaskBackgroundBeforeLoading(true);
            setBodyScrolling(false);
            modalLoadTimeout();
            const getOneIncSessionDetails = AccountBillingDetailsService.getOneIncSessionDetails(oneIncPaymentDetails.accountNumber, oneIncPaymentDetails.contactPublicId, null, null, authHeader);
            getOneIncSessionDetails.then((response) => {
                if (!response.sessionID) {
                    handleError();
                    return;
                }
                portalOne.current.managePaymentMethods({
                    customerId: response.oneIncCustomerID,
                    sessionId: response.sessionID,
                    acceptCreditCards: true,
                    acceptPrepaidCards: false,
                    clientReferenceData1: oneIncPaymentDetails.accountNumber,
                    clientReferenceData2: oneIncPaymentDetails.contactPublicId,
                    clientReferenceData4: 'AMP',
                    clientReferenceData5: removeHttp(response.bcUrl),
                    allowClosing: true,
                    displayMode: 'Modal',
                    saveOption: 'UserSelect'
                });
                
                console.log('[OneINC] Calling managePaymentMethods (manage)');
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [managePaymentMethodsEnabled]);

    if (modalLoadErrorHandled) {
        return (
            <ModalNext isOpen={modalLoadErrorHandled}
                       shouldCloseOnOverlayClick={false}
                       shouldCloseOnEsc
                       onRequestClose={handleClose}>
                <div className={styles.modalHeaderBodyContainer}>
                    <h1 className="wmic-modal-header-title">{translator(messages.paymentSystemUnavailable)}</h1>
                </div>
                <div className={styles.modalBodyContainer}>{WMICRichTextUtil.translateRichText(translator(messages.paymentSystemCurrentlyUnavailable))}</div>
                <ModalFooter>
                    <WMICButton
                        onClick={() => {
                            updateModalLoadErrorHandled(false);
                            paymentModalClosed();
                        }}
                    >
                        {translator(messages.close)}
                    </WMICButton>
                </ModalFooter>
            </ModalNext>
        );
    }

    return (
        !modalLoadErrorHandled && (
            <>
                { maskBackgroundBeforeLoading && (
                    <div id="maskBackground" className={styles.maskBackground}/>
                )}
            </>
        )
    );
};

WMICOneIncPaymentModal.propTypes = {
    oneIncPaymentDetails: PropTypes.shape({}).isRequired,
    paymentModalClosed: PropTypes.func.isRequired,
    saveComplete: PropTypes.func.isRequired,
    paymentModalLoaded: PropTypes.func,
    payNowEnabled: PropTypes.bool,
    savePaymentEnabled: PropTypes.bool,
    managePaymentMethodsEnabled: PropTypes.bool,
    updateModalLoaded: PropTypes.func.isRequired
};

WMICOneIncPaymentModal.defaultProps = {
    payNowEnabled: false,
    savePaymentEnabled: false,
    managePaymentMethodsEnabled: false,
    saveComplete: () => {},
    paymentModalLoaded: () => {}
};

export default WMICOneIncPaymentModal;